import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { colorsMapping, VariantColor } from '@repo/toolkit/colors'
import { handleKeyDown } from '@repo/toolkit/handle-key-down'
import { CustomOption, OptionType } from '@repo/toolkit/types'
import useDropdownPosition from '@repo/toolkit/use-drop-down-position'
import { useKeydown } from '@repo/toolkit/use-keydown'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useHover, useOnClickOutside } from 'usehooks-ts'

import useTranslate from '../../../../apps/backoffice/src/lib/hooks/use-translate'
import { CheckMarkIcon, ChevronDownIcon } from '../../icons'
import Badge from './badge'
import { InputTooltip } from './input/input-tooltip'
import InputWrapper, { InputWrapperProps } from './input/input-wrapper'
import Typography, { typography } from './typography'

export type SelectInputProps<T extends OptionType> = {
  /**
   * Options for the select input
   */
  options?: CustomOption<T>[]

  /**
   * Options to ignore
   */
  ignoredOptions?: T[]

  /**
   * Value of the input
   */
  selectedOption?: CustomOption<T>

  /**
   * If true, the select will have a search input for filtering the options
   */
  searchable?: boolean

  /**
   * If true, the input can accept free text
   */
  allowNewOption?: boolean

  /**
   * Additional class name for the input
   */
  className?: string

  /**
   * Icon to display on the left of the input
   */
  inputClassName?: string

  /**
   * Additional class name for the wrapper
   */
  wrapperClassName?: string

  /**
   * Size of the input
   */
  size?: 'small' | 'large'

  /**
   * ReadOnly state of the input
   * @default false
   */
  readOnly?: boolean

  /**
   * If true, the dropdown icon will be hidden
   */
  hideDropdownIcon?: boolean

  /**
   * Indicate wether the dropdown should show normal under the component or above of everything
   */
  dropDownPosition?: 'under' | 'above'

  /**
   * Callback to handle the input change
   */
  onChange?: (value: T) => void

  /**
   * Callback to handle the input text change
   */
  onTextChange?: (text: string) => void

  /**
   * Callback to handle the input blur
   */
  onBlur?: () => void

  /**
   * Label class name
   */
  labelClassName?: string

  /**
   *badge shape
   */
  mode?: 'input' | 'badge'

  /**
   * Variant of the badge
   */
  variantColor?: VariantColor

  /**
   * Tooltip content
   */
  tooltipContent?: string | ReactNode

  /**   * If true, the input is optional
   */
  optional?: boolean
  optionalText?: string
} & Omit<
  InputWrapperProps,
  | 'children'
  | 'className'
  | 'iconStyle'
  | 'iconToRight'
  | 'active'
  | 'iconToTheRight'
  | 'focused'
  | 'onBlur'
>

const SelectInput = <T extends OptionType>({
  options,
  ignoredOptions = [],
  selectedOption,
  searchable,
  className,
  wrapperClassName,
  size = 'large',
  labelClassName,
  icon,
  allowNewOption,
  hideDropdownIcon = false,
  dropDownPosition = 'under',
  mode = 'input',
  variantColor,
  tooltipContent,
  inputClassName,
  optional,
  optionalText,
  onBlur,
  onChange,
  onTextChange,
  ...props
}: SelectInputProps<T>) => {
  const { t } = useTranslate()
  const [isOpen, setIsOpen] = useState(false)
  const [availableOptions, setAvailableOptions] = useState<CustomOption<T>[]>([])
  const [inputValue, setInputValue] = useState<string>('')
  const [newOption, setNewOption] = useState<CustomOption<T>>()
  // Removed local dropdownDimensions state in favor of hook usage
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const [shouldRefocus, setShouldRefocus] = useState(true)
  const hasScrolledToSelected = useRef(false) // Add this ref

  const searchRef = useRef<HTMLInputElement>(null)
  const areOptionsRendered = useRef(false)
  const ref = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isHovered = useHover(ref)
  const isDisabled = Boolean(
    props.disabled ||
    props.readOnly ||
    (selectedOption?.value && ignoredOptions?.includes(selectedOption?.value))
  )
  const isActive = !!selectedOption || inputValue.length > 0 || isOpen

  // Create a "None" option when optional is true
  const noneOption: CustomOption<T> = useMemo(
    () => ({ label: optionalText ?? t('common.select.none'), value: null as unknown as T }),
    [optionalText, t]
  )

  // Prepend the None option to the options list when optional is true
  const optionsWithNone = useMemo(() => {
    if (!optional) return options ?? []
    return [noneOption, ...(options ?? [])]
  }, [optional, options, noneOption])

  const toggle = () => {
    setIsOpen((prev) => {
      if (!prev) setHighlightedIndex(0)
      return !prev
    })
  }

  const handleChange = (value: T) => {
    onChange?.(value)
    onBlur?.()
  }

  const handleOptionClick = (option: CustomOption<T>) => {
    setInputValue(option.label)
    handleChange?.(option.value)
    setIsOpen(false)
    setHighlightedIndex(-1)
    setNewOption(undefined)
  }

  const handleAddNewOption = (text: string) => {
    const newOption: CustomOption<T> = {
      label: text,
      value: text as unknown as T,
      custom: true
    }

    setNewOption(newOption)
    setInputValue(text)
    handleChange?.(newOption.value)
    setIsOpen(false)
    setHighlightedIndex(-1)
  }

  const handleSearch = (text: string) => {
    if (!searchable) return
    onTextChange?.(text)
    setInputValue(text)

    const filteredOptions = optionsWithNone.filter((option) =>
      option?.label?.toLowerCase().includes(text?.toLowerCase())
    )

    setAvailableOptions(filteredOptions)
    setHighlightedIndex(-1)
  }

  const handleClickOutside = () => {
    if (!isOpen) return

    setIsOpen(false)
    setHighlightedIndex(-1)

    if (allowNewOption && newOption?.label?.toLowerCase() === inputValue.toLowerCase()) {
      handleChange?.(newOption.value)
      onBlur?.()
      return
    }

    const textValue = optionsWithNone.find(
      (option) => option?.label?.toLowerCase() === inputValue?.toLowerCase()
    )
    if (textValue) handleChange?.(textValue.value)
    else if (allowNewOption && inputValue.trim() === '') {
      // Allow clearing the field when allowNewOption is true
      handleChange?.('' as unknown as T)
      setInputValue('')
    } else if (selectedOption?.value) setInputValue(selectedOption.label)
    else if (!selectedOption?.value && newOption && allowNewOption) setInputValue(newOption.label)
    else setInputValue('')

    onBlur?.()
  }

  const hasNewOption = useMemo(() => {
    return !!(
      allowNewOption &&
      inputValue.trim().length > 0 &&
      newOption?.label?.toLowerCase() !== inputValue.toLowerCase() &&
      !availableOptions.find((o) => o.label?.toLowerCase() === inputValue.toLowerCase())
    )
  }, [inputValue, newOption, availableOptions])

  // When the dropdown opens, scroll the currently selected option into view.
  useEffect(() => {
    if (!isOpen) {
      hasScrolledToSelected.current = false // Reset when closing
      return
    }

    // Only auto-scroll on initial open
    if (hasScrolledToSelected.current) return

    // Use the actual rendered list: prefer availableOptions (filtered/search) otherwise fall back to options
    const baseOptions =
      availableOptions && availableOptions.length > 0 ? availableOptions : (options ?? [])
    const renderedOptions = baseOptions.filter((o) => !ignoredOptions?.includes(o.value))

    const selectedIndexInRendered = renderedOptions.findIndex(
      (option) => option.value === selectedOption?.value
    )
    if (selectedIndexInRendered === -1) return

    // If a "new option" row is present it sits at index 0, so shift by +1
    const adjustedIndex = hasNewOption ? selectedIndexInRendered + 1 : selectedIndexInRendered

    // Wait for next frame to ensure DOM children are rendered and any CSS transforms applied
    requestAnimationFrame(() => {
      if (!dropdownRef.current) return
      const container = dropdownRef.current
      const selectedElement = container.children[adjustedIndex] as HTMLElement | undefined
      if (selectedElement && container) {
        // Center the selected element in the dropdown container vertically.
        const elTop = selectedElement.offsetTop
        const elHeight = selectedElement.offsetHeight
        const containerHeight = container.clientHeight
        const desiredScroll = elTop - containerHeight / 2 + elHeight / 2
        const maxScroll = Math.max(0, container.scrollHeight - containerHeight)
        const clamped = Math.max(0, Math.min(desiredScroll, maxScroll))
        container.scrollTop = clamped

        // Also set the highlighted index so keyboard navigation starts on the selected item
        setHighlightedIndex(adjustedIndex)
        hasScrolledToSelected.current = true // Mark as scrolled
      }
    })
  }, [isOpen, availableOptions, options, selectedOption, hasNewOption, ignoredOptions])

  useOnClickOutside(ref, handleClickOutside)

  useEffect(() => {
    if (!selectedOption?.label && allowNewOption && newOption) {
      setInputValue(newOption.label)
    } else setInputValue(selectedOption?.label ?? '')
  }, [selectedOption?.value])

  // The label of the same value can arrive later (e.g. once the options list is loaded)
  useEffect(() => {
    if (!isOpen && selectedOption?.label) setInputValue(selectedOption.label)
  }, [selectedOption?.label])

  useEffect(() => {
    setAvailableOptions(optionsWithNone)
  }, [optionsWithNone])

  useEffect(() => {
    if (!areOptionsRendered.current && selectedOption && options) {
      setInputValue(selectedOption?.label)
      areOptionsRendered.current = true
    }
  }, [selectedOption?.value, options])

  useKeydown(() => {
    if (isDisabled) return
    setIsOpen(false)
    handleClickOutside()
  }, ['Tab'])

  useKeydown(() => {
    if (isDisabled || !hasNewOption) return

    // Adding new option on enter key
    handleAddNewOption(inputValue)
  }, ['Enter'])

  useEffect(() => {
    if (!isDisabled) return

    setIsOpen(false)
    setHighlightedIndex(-1)
    searchRef.current?.blur()
  }, [isDisabled])

  // Close the above dropdown when scrolling to avoid overflow and leaving the screen space
  useEffect(() => {
    if (dropDownPosition !== 'above') return

    const handleScroll = (e: Event) => {
      const target = e.target as Node

      if (
        (ref.current && ref.current.contains(target)) ||
        (dropdownRef.current && dropdownRef.current.contains(target))
      ) {
        return
      }
      setIsOpen(false)
    }

    window.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [dropDownPosition])

  // Use the custom hook to get dropdown dimensions when needed
  const dropdownDimensions = useDropdownPosition(ref, isOpen, dropDownPosition)

  return (
    <div className="flex flex-row gap-2 items-center relative">
      <div
        ref={ref}
        className={cn('relative w-full outline-none', {
          'pointer-events-none': isDisabled
        })}
        onKeyDown={(e) => {
          if (isDisabled) return

          // Enter on a typed label that matches an option picks it, so it must not also reach the surrounding container
          if (e.key === 'Enter' && isOpen && highlightedIndex === -1 && inputValue.trim()) {
            const textValue = optionsWithNone.find(
              (option) => option?.label?.toLowerCase() === inputValue.toLowerCase()
            )
            if (textValue) {
              e.preventDefault()
              e.nativeEvent.stopPropagation()
              handleOptionClick(textValue)
              return
            }
          }

          handleKeyDown<T>(e, {
            isOpen,
            highlightedIndex,
            inputValue,
            availableOptions,
            ignoredOptions,
            hasNewOption,
            setIsOpen,
            setHighlightedIndex,
            handleAddNewOption: (input) => handleAddNewOption(input),
            handleOptionClick: (option) => handleOptionClick(option),
            onBlur: () => {
              setIsOpen(false)
              onBlur?.()
            }
          })
        }}
        onBlur={(e) => {
          if (isDisabled) return

          const relatedTarget = e.relatedTarget as Node | null

          if (relatedTarget !== searchRef.current && !ref.current?.contains(relatedTarget)) {
            // Programmatically blur the search input if it's focused
            if (searchRef.current && document.activeElement === searchRef.current) {
              searchRef.current.blur()
              setShouldRefocus(false) // Prevent refocusing
            }

            handleClickOutside()
          }
        }}
        onFocus={() => {
          if (isDisabled) return

          if (shouldRefocus) setIsOpen(true)
          else setShouldRefocus(false)
        }}
        tabIndex={isDisabled || searchable || isHovered ? undefined : 0}
      >
        {mode === 'input' && (
          <InputWrapper
            {...props}
            active={isActive}
            focused={isOpen}
            className={className}
            icon={icon}
            labelClassName={labelClassName}
            wrapperClassName={wrapperClassName}
          >
            <div
              className={cn('flex items-center justify-between gap-2', {
                'py-0.5': size === 'small'
              })}
              onClick={(e) => {
                if (props.disabled || props.readOnly) return
                e.stopPropagation()
                toggle()
              }}
            >
              {searchable ? (
                <input
                  ref={searchRef}
                  type="text"
                  value={inputValue}
                  style={typography['label-medium']}
                  disabled={props.disabled}
                  readOnly={props.readOnly}
                  className={cn(
                    'outline-none flex-1 bg-transparent disabled:bg-transparent disabled:opacity-100',
                    {
                      'text-grey-light-active ': props.disabled
                    },
                    inputClassName
                  )}
                  onChange={(e) => handleSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={() => {
                    if (isDisabled) return

                    setIsOpen(true)

                    // Reset the list to show all options immediately
                    if (newOption && availableOptions.find((o) => o.custom)) {
                      setAvailableOptions([newOption, ...optionsWithNone])
                    } else {
                      setAvailableOptions(optionsWithNone)

                      if (selectedOption) {
                        const index = optionsWithNone.findIndex(
                          (o) => o.value === selectedOption.value
                        )
                        setHighlightedIndex(index >= 0 ? index : 0)
                      } else {
                        setHighlightedIndex(0)
                      }
                    }
                  }}
                  onBlur={() => {
                    if (isDisabled || isHovered) return
                    handleClickOutside()
                  }}
                />
              ) : (
                <Typography
                  variant="label-medium"
                  className={cn({ 'text-grey-light-active': props.disabled })}
                >
                  {selectedOption?.custom || selectedOption?.label}
                </Typography>
              )}

              {!hideDropdownIcon && (
                <ChevronDownIcon
                  className={cn('transition-transform duration-200', {
                    'transform rotate-180': isOpen
                  })}
                  color={
                    props.disabled ? tailwindColors.grey.light.active : tailwindColors.grey.DEFAULT
                  }
                  width="18px"
                  height="18px"
                />
              )}
            </div>
          </InputWrapper>
        )}
        {mode === 'badge' && (
          <Badge
            text={inputValue}
            variant={variantColor}
            className={className}
            onClick={() => {
              if (isDisabled) return
              toggle()
            }}
            onBlur={() => {
              if (isDisabled) return
              handleClickOutside()
            }}
            icon={
              <ChevronDownIcon
                className={cn('transition-transform duration-200', {
                  'transform rotate-180': isOpen
                })}
                color={
                  props.disabled
                    ? tailwindColors.grey.light.active
                    : variantColor
                      ? colorsMapping[variantColor].text
                      : tailwindColors.grey.DEFAULT
                }
                width="18px"
                height="18px"
              />
            }
          />
        )}

        <div
          ref={dropdownRef}
          tabIndex={99999}
          className={cn(
            'w-full bg-white shadow rounded mt-1 duration-150 ease-out transform origin-top max-h-60 overflow-y-auto',
            isOpen ? 'scale-y-100' : 'scale-y-0 opacity-0 pointer-events-none',
            searchable ? 'pb-2' : 'py-2',
            dropDownPosition === 'above' ? 'fixed z-50' : 'absolute z-20'
          )}
          style={{
            width: dropDownPosition === 'above' ? dropdownDimensions?.width : '100%',
            top: dropDownPosition === 'above' ? dropdownDimensions?.top : '100%',
            left: dropDownPosition === 'above' ? dropdownDimensions?.left : undefined,
            transition: 'opacity 0.15s ease-out, transform 0.15s ease-out'
          }}
        >
          {hasNewOption && (
            <div
              className={cn(
                'flex items-center justify-between gap-2 w-full px-4 py-2 text-left transition-colors',
                highlightedIndex === 0 ? 'bg-white-active' : 'hover:bg-white-active'
              )}
              onClick={() => handleAddNewOption(inputValue)}
            >
              <Typography variant="label-semibold">
                {t('common.select.add.option', { value: inputValue })}
              </Typography>
            </div>
          )}

          {availableOptions
            .filter((option) => !ignoredOptions?.includes(option.value))
            .map((option, index) => {
              const isHighlighted = hasNewOption
                ? index + 1 === highlightedIndex
                : index === highlightedIndex
              return (
                <div
                  key={index}
                  title={option.label}
                  className={cn(
                    'flex items-center justify-between gap-2 w-full px-4 py-2 text-left transition-colors',
                    isHighlighted ? 'bg-white-active' : 'hover:bg-white-active'
                  )}
                  onClick={() => handleOptionClick(option)}
                >
                  <Typography variant="label-semibold">
                    {option?.custom || option.label}
                  </Typography>
                  {selectedOption?.value === option.value && (
                    <CheckMarkIcon color={tailwindColors.primary.DEFAULT} height="16" width="16" />
                  )}
                </div>
              )
            })}
        </div>
      </div>

      {tooltipContent && (
        <div className="absolute top-2.5 right-9">
          <InputTooltip>{tooltipContent}</InputTooltip>
        </div>
      )}
    </div>
  )
}

export default SelectInput
